/**
 * API client for Cloud Run backend services.
 *
 * Routes requests to the correct service:
 * - Transaction Search: /lookup/*
 * - Dispute Processor: /disputes/*, /ingestion/*, /kombatix/*, /cases/*
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Service = 'search' | 'processor' | 'webhooks';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

let companyId = '';
let siteId = '';
let getAuthToken: (() => Promise<string | null>) | null = null;

/** Configure the company context for all API calls */
export function setCompanyContext(company: string, site?: string) {
  companyId = company;
  siteId = site || '';
}

/** Configure the auth token provider */
export function setAuthProvider(provider: () => Promise<string | null>) {
  getAuthToken = provider;
}

function getBaseUrl(service: Service): string {
  if (service === 'search') {
    return import.meta.env.VITE_TRANSACTION_SEARCH_URL || 'http://localhost:8080';
  }
  if (service === 'webhooks') {
    return import.meta.env.VITE_WEBHOOK_INGESTION_URL || 'http://localhost:8082';
  }
  return import.meta.env.VITE_DISPUTE_PROCESSOR_URL || 'http://localhost:8081';
}

async function request<T>(service: Service, path: string, options: RequestOptions = {}): Promise<T> {
  const baseUrl = getBaseUrl(service);
  const url = `${baseUrl}${path}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (companyId) {
    headers['X-Company-ID'] = companyId;
  }
  if (siteId) {
    headers['X-Site-ID'] = siteId;
  }

  if (getAuthToken) {
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers: { ...headers, ...(options.headers as Record<string, string>) },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, body.error || body.message || `HTTP ${response.status}`, body);
  }

  return response.json();
}

/** GET request to a backend service */
export function get<T>(service: Service, path: string): Promise<T> {
  return request<T>(service, path, { method: 'GET' });
}

/** POST request to a backend service */
export function post<T>(service: Service, path: string, body?: unknown): Promise<T> {
  return request<T>(service, path, { method: 'POST', body });
}

/** PUT request to a backend service */
export function put<T>(service: Service, path: string, body?: unknown): Promise<T> {
  return request<T>(service, path, { method: 'PUT', body });
}

/** PATCH request to a backend service */
export function patch<T>(service: Service, path: string, body?: unknown): Promise<T> {
  return request<T>(service, path, { method: 'PATCH', body });
}

/** DELETE request to a backend service */
export function del<T>(service: Service, path: string): Promise<T> {
  return request<T>(service, path, { method: 'DELETE' });
}

/** A downloaded file, with the response headers that describe it. */
export interface BlobDownload {
  blob: Blob;
  /** From `Content-Disposition: attachment; filename="..."`; null when absent or unreadable. */
  filename: string | null;
  headers: Headers;
}

/** The filename in a `Content-Disposition` header, quoted or bare. */
export function filenameFromContentDisposition(value: string | null): string | null {
  if (!value) return null;
  const m = /filename="([^"]+)"/i.exec(value) ?? /filename=([^;\s]+)/i.exec(value);
  return m ? m[1]! : null;
}

/** GET request that returns a raw Blob (for file downloads), with its headers */
export async function fetchBlob(service: Service, path: string): Promise<BlobDownload> {
  const baseUrl = getBaseUrl(service);
  const url = `${baseUrl}${path}`;

  const headers: Record<string, string> = {};

  if (companyId) {
    headers['X-Company-ID'] = companyId;
  }
  if (siteId) {
    headers['X-Site-ID'] = siteId;
  }

  if (getAuthToken) {
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, body.error || body.message || `HTTP ${response.status}`, body);
  }

  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(response.headers.get('content-disposition')),
    headers: response.headers,
  };
}
